import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface HealthCheckEntry {
  checked_at: string;
  response_time_ms?: number | null;
  is_healthy: boolean;
  status_code?: number | null;
}

interface ResponseTimeChartProps {
  healthChecks: HealthCheckEntry[] | null;
}

interface ChartDataPoint {
  timestamp: string;
  responseTime: number | null | undefined;
  isHealthy: boolean;
  statusCode: number | null | undefined;
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { payload: ChartDataPoint }[]; label?: string }) => {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0].payload;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{label}</p>
      <p className="text-sm text-gray-700 dark:text-gray-300">
        Response Time: {data.responseTime == null ? 'N/A' : `${data.responseTime.toFixed(0)}ms`}
      </p>
      <p className="text-sm text-gray-700 dark:text-gray-300">
        Status Code: {data.statusCode ?? 'N/A'}
      </p>
      <p className={`text-sm font-medium ${data.isHealthy ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {data.isHealthy ? 'Healthy' : 'Unhealthy'}
      </p>
    </div>
  );
};

const CustomDot = (props: { cx?: number; cy?: number; payload?: ChartDataPoint }) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null || !payload) return null;
  const fill = payload.isHealthy ? '#10b981' : '#ef4444';
  return <circle cx={cx} cy={cy} r={4} fill={fill} stroke="none" />;
};

export default function ResponseTimeChart({ healthChecks }: ResponseTimeChartProps) {
  if (!healthChecks || healthChecks.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Response Time
        </h3>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No response time data available
        </div>
      </div>
    );
  }

  // Sort by checked_at ascending for chronological display
  const sortedChecks = [...healthChecks].sort(
    (a, b) => new Date(a.checked_at).getTime() - new Date(b.checked_at).getTime()
  );

  const chartData: ChartDataPoint[] = sortedChecks.map((check) => ({
    timestamp: new Date(check.checked_at).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    responseTime: check.response_time_ms,
    isHealthy: check.is_healthy,
    statusCode: check.status_code,
  }));

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Response Time
      </h3>
      <div style={{ width: '100%', height: 400 }}>
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis
              dataKey="timestamp"
              className="text-gray-600 dark:text-gray-400"
              tick={{ fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis
              className="text-gray-600 dark:text-gray-400"
              tick={{ fontSize: 12 }}
              label={{ value: 'ms', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="responseTime"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={{ r: 6 }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
